/**
 * MaterialManagementDashboard.js
 *
 * Material Management dashboard (review item 21.iii.8). Master view lives under
 * Procurement; the same page is reachable from Estimation and Project Management
 * scoped by estimate / job number (?estimate= / ?job= query params).
 *
 * Every requisition sent to purchasing from the MTO lands here as one row per
 * line. The purchaser records what is already in stock, raises POs for the
 * remainder, tracks received quantities, and can email selected lines straight
 * to a supplier (same QUANTITY|DESCRIPTION|PRICE|UNIT|TOTAL NET Excel as the
 * requisition) without downloading/forwarding manually.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { useLocation } from 'react-router-dom';
import { authenticatedApiCall } from '../../utils/apiClient';
import { ListenButton } from '../common/AudioGuide';
import HelpWithVideoTour from '../Help/HelpWithVideoTour';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const remainingToPo = (r) => Math.max(num(r.qty_requested) - num(r.qty_in_stock) - num(r.po_qty), 0);

const STATUS_OPTIONS = ['requested', 'ordered', 'partially_received', 'received', 'cancelled'];
const STATUS_STYLES = {
  requested: { background: '#fef3c7', color: '#92400e' },
  ordered: { background: '#dbeafe', color: '#1e40af' },
  partially_received: { background: '#e0e7ff', color: '#3730a3' },
  received: { background: '#d1fae5', color: '#065f46' },
  cancelled: { background: '#f3f4f6', color: '#6b7280' },
};

// Purchaser-editable fields; everything else on the row comes from the MTO requisition.
const EDIT_FIELDS = [
  { key: 'qty_in_stock', label: 'In Stock', type: 'number', width: 80 },
  { key: 'po_number_or_fi', label: 'PO # / FI', type: 'text', width: 110 },
  { key: 'po_qty', label: 'PO Qty', type: 'number', width: 80 },
  { key: 'qty_received', label: 'Received', type: 'number', width: 80 },
];

const th = { padding: '8px 10px', whiteSpace: 'nowrap', fontWeight: 600 };
const td = { padding: '6px 10px', verticalAlign: 'top' };

const MaterialManagementDashboard = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('materialMgmtDashboard', 'global', true);
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null); // { type: 'success'|'error', text }
  const [estimateFilter, setEstimateFilter] = useState(urlParams.get('estimate') || '');
  const [jobFilter, setJobFilter] = useState(urlParams.get('job') || '');
  const [reqFilter, setReqFilter] = useState('');
  const [textFilter, setTextFilter] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [savingId, setSavingId] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // Email-supplier modal state
  const [emailModal, setEmailModal] = useState(false);
  const [supplierEmail, setSupplierEmail] = useState('');
  const [supplierName, setSupplierName] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const qp = [];
      if (estimateFilter.trim()) qp.push(`estimateNumber=${encodeURIComponent(estimateFilter.trim())}`);
      if (jobFilter.trim()) qp.push(`jobNumber=${encodeURIComponent(jobFilter.trim())}`);
      if (reqFilter.trim()) qp.push(`requisitionNo=${encodeURIComponent(reqFilter.trim())}`);
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/material-management${qp.length ? `?${qp.join('&')}` : ''}`);
      // Only requisition-born rows carry a description; legacy stock rows without one still show via part number.
      setRows(resp.data?.data || []);
    } catch (err) {
      setNotice({ type: 'error', text: err?.response?.data?.message || 'Failed to load material data.' });
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, estimateFilter, jobFilter, reqFilter]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const visibleRows = useMemo(() => {
    const t = textFilter.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((r) =>
      [r.part_number, r.description, r.requisition_no, r.supplier, r.po_number_or_fi, r.status]
        .some((v) => v && String(v).toLowerCase().includes(t)));
  }, [rows, textFilter]);

  const kpis = useMemo(() => {
    const outstanding = visibleRows.filter((r) => remainingToPo(r) > 0 && (r.status || 'requested') !== 'cancelled');
    return {
      lines: visibleRows.length,
      requisitions: new Set(visibleRows.map((r) => r.requisition_no).filter(Boolean)).size,
      outstanding: outstanding.length,
      received: visibleRows.filter((r) => (r.status || '') === 'received' || (num(r.qty_received) >= num(r.qty_requested) && num(r.qty_requested) > 0)).length,
    };
  }, [visibleRows]);

  const saveField = async (row, field, rawValue) => {
    const value = rawValue === '' ? null : rawValue;
    const current = row[field];
    if (String(current ?? '') === String(rawValue ?? '')) return;
    setSavingId(row.id);
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/material-management/${row.id}`, { [field]: value });
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, [field]: value } : r)));
    } catch (err) {
      setNotice({ type: 'error', text: err?.response?.data?.message || `Failed to save ${field}.` });
      fetchRows();
    } finally {
      setSavingId(null);
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === visibleRows.length ? new Set() : new Set(visibleRows.map((r) => r.id))));
  };

  const sendSupplierEmail = async () => {
    setEmailSending(true);
    setNotice(null);
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/material-management/email-supplier', {
        ids: Array.from(selected),
        supplier_email: supplierEmail.trim(),
        supplier_name: supplierName.trim() || undefined,
        message: emailMessage.trim() || undefined,
      });
      setNotice({ type: 'success', text: resp.data?.message || 'Email sent.' });
      setEmailModal(false);
      setSelected(new Set());
      setSupplierEmail(''); setSupplierName(''); setEmailMessage('');
      fetchRows();
    } catch (err) {
      setNotice({ type: 'error', text: err?.response?.data?.message || 'Failed to email supplier.' });
    } finally {
      setEmailSending(false);
    }
  };

  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '3px 6px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: '0.8rem' };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto', padding: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, color: '#111827' }}>📦 Material Management</h2>
        <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
        <button onClick={() => setShowHelp((s) => !s)}
          style={{ border: '1px solid #d1d5db', background: 'white', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontSize: '0.78rem', color: '#374151' }}>
          {showHelp ? 'Hide help' : '❓ Help'}
        </button>
      </div>
      <p style={{ margin: '0.25rem 0 1rem', color: '#6b7280', fontSize: '0.88rem' }}>
        Every requisition sent to purchasing from the MTO lands here. Record stock, raise POs for the remainder, track receipts.
      </p>

      {showHelp && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '1rem', fontSize: '0.84rem', color: '#1e3a8a', lineHeight: 1.55 }}>
          <HelpWithVideoTour guideId="material-management-help" title="Material Management Help" accent="#2563eb">

          <strong>How this dashboard works</strong>
          <ListenButton guideId="material-management-help" title="Material Management Help" accent="#2563eb" />
          <ul style={{ margin: '0.4rem 0 0', paddingLeft: '1.2rem' }}>
            <li>When a TC sends an MTO to purchasing, each requisition line appears here with its <em>Requested</em> quantity (purchasing qty incl. waste).</li>
            <li><strong>In Stock</strong>: enter what is already on hand — <strong>Rem. to PO</strong> = Requested − In Stock − PO Qty updates automatically, so you only order the remainder.</li>
            <li><strong>PO # / PO Qty / Received</strong>: track the purchase order and receipts per line; set the <strong>Status</strong> as it progresses.</li>
            <li><strong>Email supplier</strong>: tick lines and click "✉ Email supplier" to send the order list (Excel) straight to a supplier — no manual download/forward needed.</li>
            <li>The master view (all estimates/jobs) lives under <strong>Procurement</strong>; from Estimation or Project Management the same page opens filtered by estimate / job.</li>
          </ul>

          </HelpWithVideoTour>
        </div>
      )}

      {notice && (
        <div style={{
          padding: '0.6rem 0.9rem', borderRadius: 8, marginBottom: '0.9rem', fontSize: '0.85rem',
          background: notice.type === 'error' ? '#fef2f2' : '#ecfdf5',
          border: `1px solid ${notice.type === 'error' ? '#fecaca' : '#a7f3d0'}`,
          color: notice.type === 'error' ? '#b91c1c' : '#065f46',
          display: 'flex', justifyContent: 'space-between', gap: 10,
        }}>
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit', fontWeight: 700 }}>×</button>
        </div>
      )}

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: '1rem' }}>
        {[
          { label: 'Requisitions', value: kpis.requisitions, color: '#1d4ed8' },
          { label: 'Material lines', value: kpis.lines, color: '#111827' },
          { label: 'Outstanding (Rem. to PO)', value: kpis.outstanding, color: '#b45309' },
          { label: 'Fully received', value: kpis.received, color: '#047857' },
        ].map((k) => (
          <div key={k.label} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, padding: '0.7rem 0.9rem', boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <div style={{ fontSize: '0.72rem', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>{k.label}</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 700, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filters + actions */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.8rem' }}>
        <input value={estimateFilter} onChange={(e) => setEstimateFilter(e.target.value)} placeholder="Estimate #"
          style={{ ...inputStyle, width: 140, padding: '5px 8px' }} />
        <input value={jobFilter} onChange={(e) => setJobFilter(e.target.value)} placeholder="Job #"
          style={{ ...inputStyle, width: 120, padding: '5px 8px' }} />
        <input value={reqFilter} onChange={(e) => setReqFilter(e.target.value)} placeholder="Requisition #"
          style={{ ...inputStyle, width: 160, padding: '5px 8px' }} />
        <input value={textFilter} onChange={(e) => setTextFilter(e.target.value)} placeholder="Search part / description / PO / supplier…"
          style={{ ...inputStyle, width: 260, padding: '5px 8px' }} />
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setEmailModal(true)}
          disabled={selected.size === 0}
          title={selected.size === 0 ? 'Select lines first' : `Email ${selected.size} line(s) to a supplier`}
          style={{
            background: selected.size ? '#0d6efd' : '#e5e7eb', color: selected.size ? 'white' : '#9ca3af',
            border: 'none', borderRadius: 6, padding: '6px 14px', cursor: selected.size ? 'pointer' : 'not-allowed', fontSize: '0.83rem', fontWeight: 600,
          }}>
          ✉ Email supplier{selected.size ? ` (${selected.size})` : ''}
        </button>
        <button onClick={fetchRows} style={{ border: '1px solid #d1d5db', background: 'white', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: '0.83rem' }}>
          ↻ Refresh
        </button>
      </div>

      {loading ? (
        <div style={{ padding: '2.5rem', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
      ) : visibleRows.length === 0 ? (
        <div style={{ padding: '2.5rem', textAlign: 'center', color: '#6b7280', background: 'white', border: '1px dashed #d1d5db', borderRadius: 10 }}>
          No material lines{estimateFilter || jobFilter || reqFilter || textFilter ? ' match the filters' : ' yet — they appear when an MTO is sent to purchasing'}.
        </div>
      ) : (
        <div style={{ overflowX: 'auto', background: 'white', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
                <th style={th}>
                  <input type="checkbox" checked={visibleRows.length > 0 && selected.size === visibleRows.length} onChange={toggleSelectAll} />
                </th>
                <th style={th}>Requisition</th>
                <th style={th}>Estimate / Job</th>
                <th style={th}>Part #</th>
                <th style={{ ...th, minWidth: 260 }}>Description</th>
                <th style={th}>UM</th>
                <th style={{ ...th, textAlign: 'right' }}>Requested</th>
                {EDIT_FIELDS.map((f) => <th key={f.key} style={{ ...th, textAlign: f.type === 'number' ? 'right' : 'left' }}>{f.label}</th>)}
                <th style={{ ...th, textAlign: 'right' }}>Rem. to PO</th>
                <th style={th}>Status</th>
                <th style={th}>Supplier</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const rem = remainingToPo(r);
                const status = r.status || 'requested';
                return (
                  <tr key={r.id} style={{ borderBottom: '1px solid #f3f4f6', opacity: savingId === r.id ? 0.6 : 1, background: selected.has(r.id) ? '#eff6ff' : undefined }}>
                    <td style={td}>
                      <input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} />
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{r.requisition_no || '—'}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.job_number || r.estimate_number || '—'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{r.part_number || '—'}</td>
                    <td style={td}>{r.description || '—'}</td>
                    <td style={td}>{r.um || '—'}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{num(r.qty_requested).toLocaleString()}</td>
                    {EDIT_FIELDS.map((f) => (
                      <td key={f.key} style={{ ...td, width: f.width }}>
                        <input
                          type={f.type}
                          defaultValue={r[f.key] ?? ''}
                          onBlur={(e) => saveField(r, f.key, e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          style={{ ...inputStyle, textAlign: f.type === 'number' ? 'right' : 'left' }}
                        />
                      </td>
                    ))}
                    <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: rem > 0 ? '#b45309' : '#047857' }}>
                      {rem.toLocaleString()}
                    </td>
                    <td style={td}>
                      <select
                        value={status}
                        onChange={(e) => saveField(r, 'status', e.target.value)}
                        style={{ ...inputStyle, ...(STATUS_STYLES[status] || {}), fontWeight: 600, border: '1px solid transparent', borderRadius: 999, padding: '3px 8px' }}>
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                      </select>
                    </td>
                    <td style={{ ...td, width: 150 }}>
                      <input
                        type="text"
                        defaultValue={r.supplier ?? ''}
                        placeholder={r.supplier_email || ''}
                        onBlur={(e) => saveField(r, 'supplier', e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                        style={inputStyle}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Email supplier modal */}
      {emailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => !emailSending && setEmailModal(false)}>
          <div style={{ background: 'white', borderRadius: 12, padding: '1.25rem 1.4rem', width: 460, maxWidth: '92vw', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 0.3rem' }}>✉ Email supplier</h3>
            <p style={{ margin: '0 0 0.9rem', color: '#6b7280', fontSize: '0.84rem' }}>
              Sends the {selected.size} selected line(s) as an order-request Excel (quantity = remaining to PO). You are CC'd.
            </p>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>Supplier email *</label>
            <input value={supplierEmail} onChange={(e) => setSupplierEmail(e.target.value)} placeholder="orders@supplier.com"
              style={{ ...inputStyle, padding: '7px 9px', marginBottom: 10 }} />
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>Supplier name</label>
            <input value={supplierName} onChange={(e) => setSupplierName(e.target.value)} placeholder="e.g. Wolseley"
              style={{ ...inputStyle, padding: '7px 9px', marginBottom: 10 }} />
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: 4 }}>Message (optional)</label>
            <textarea value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} rows={3}
              placeholder="Added to the email body…" style={{ ...inputStyle, padding: '7px 9px', resize: 'vertical', marginBottom: 14 }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setEmailModal(false)} disabled={emailSending}
                style={{ border: '1px solid #d1d5db', background: 'white', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: '0.85rem' }}>
                Cancel
              </button>
              <button onClick={sendSupplierEmail} disabled={emailSending || !supplierEmail.trim()}
                style={{ background: emailSending || !supplierEmail.trim() ? '#93c5fd' : '#0d6efd', color: 'white', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: emailSending ? 'wait' : 'pointer', fontSize: '0.85rem', fontWeight: 600 }}>
                {emailSending ? 'Sending…' : 'Send email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MaterialManagementDashboard;
